import { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { ConfigNeeded } from "@/components/ConfigNeeded";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MessageCircle, Loader2, Send } from "lucide-react";
import { toast } from "sonner";

export default function MesSms() {
  const [status, setStatus] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [provider, setProvider] = useState("");
  const [sender, setSender] = useState("");
  const [to, setTo] = useState("");
  const [message, setMessage] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: st }, { data: msgs }] = await Promise.all([api.get("/sms/status"), api.get("/sms/messages")]);
      setStatus(st); setRows(msgs); setProvider(st.provider || ""); setSender(st.sender || "");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveSettings = async () => {
    try { await api.put("/settings", { sms_provider: provider, sms_sender: sender }); toast.success("Réglages SMS enregistrés"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const send = async () => {
    try { await api.post("/sms/send", { to, message }); toast.success("SMS envoyé"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <PageHeader title="Mes SMS" subtitle="Envoi et historique de SMS clients" icon={MessageCircle} />

      {loading ? <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-emerald-600" /></div> : (
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="space-y-4">
          {!status?.configured && (
            <ConfigNeeded title="Configuration nécessaire" testid="sms-config-needed">
              Aucun fournisseur SMS n'est connecté. Ajoutez la clé <span className="font-mono">SMS_API_KEY</span> dans l'onglet Secrets, puis renseignez votre expéditeur ci-dessous. Aucun SMS n'est simulé tant que l'intégration n'est pas active.
            </ConfigNeeded>
          )}
          <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-3" data-testid="sms-settings">
            <div className="font-heading font-semibold text-slate-900">Réglages du fournisseur</div>
            <div className="space-y-1.5"><Label className="text-xs text-slate-500">Fournisseur</Label><Input data-testid="sms-provider" placeholder="Ex. Twilio, Brevo…" value={provider} onChange={(e)=>setProvider(e.target.value)} className="bg-slate-50 border-slate-200" /></div>
            <div className="space-y-1.5"><Label className="text-xs text-slate-500">Expéditeur (nom ou numéro)</Label><Input data-testid="sms-sender" value={sender} onChange={(e)=>setSender(e.target.value)} className="bg-slate-50 border-slate-200" /></div>
            <Button data-testid="sms-save" onClick={saveSettings} variant="outline" className="border-slate-200">Enregistrer les réglages</Button>
            <p className="text-[11px] text-slate-400">Statut : {status?.configured ? "Connecté" : "Clé API absente (Configuration nécessaire)"}</p>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-3" data-testid="sms-compose">
            <div className="font-heading font-semibold text-slate-900">Nouveau SMS</div>
            <div className="space-y-1.5"><Label className="text-xs text-slate-500">Destinataire</Label><Input data-testid="sms-to" placeholder="+33…" value={to} onChange={(e)=>setTo(e.target.value)} className="bg-slate-50 border-slate-200" /></div>
            <div className="space-y-1.5"><Label className="text-xs text-slate-500">Message</Label><textarea data-testid="sms-message" rows={3} value={message} onChange={(e)=>setMessage(e.target.value)} className="w-full rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm" /></div>
            <Button data-testid="sms-send" onClick={send} className="bg-emerald-500 hover:bg-emerald-600 text-black font-semibold"><Send className="w-4 h-4 mr-1.5" />Envoyer</Button>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-5">
          <div className="font-heading font-semibold text-slate-900 mb-3">Historique</div>
          {rows.length === 0 ? <div className="text-sm text-slate-500" data-testid="sms-empty">Aucun SMS pour le moment.</div> : (
            <div className="space-y-2" data-testid="sms-list">
              {rows.map((m) => (
                <div key={m.id} className="border border-slate-100 rounded-lg p-3">
                  <div className="text-sm text-slate-900">{m.to}</div>
                  <div className="text-xs text-slate-500">{m.message}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      )}
    </div>
  );
}
